import { getNativeBridge } from './nativeBridge';

const REFRESH_TOKEN_KEY = 'courtvibe_refresh_token';
const TOKEN_EXPIRY_KEY = 'courtvibe_token_expiry';

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

export function saveRefreshToken(token: string, expiresAt: string): void {
  try {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
    localStorage.setItem(TOKEN_EXPIRY_KEY, expiresAt);
  } catch (e) {
    console.warn('Failed to save refresh token to localStorage:', e);
  }
}

export function getRefreshToken(): string | null {
  try {
    const token = localStorage.getItem(REFRESH_TOKEN_KEY);
    const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
    
    if (!token || !expiry) return null;
    
    if (new Date(expiry) < new Date()) {
      clearRefreshToken();
      return null;
    }
    
    return token;
  } catch (e) {
    return null;
  }
}

export function clearRefreshToken(): void {
  try {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
  } catch (e) {
    console.warn('Failed to clear refresh token:', e);
  }
}

export function hasRefreshToken(): boolean {
  return getRefreshToken() !== null;
}

export async function attemptAutoLogin(): Promise<boolean> {
  if (isRefreshing) {
    return refreshPromise || Promise.resolve(false);
  }
  
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return false;
  }
  
  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const response = await fetch('/api/auth/auto-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
        credentials: 'include',
      });
      
      if (response.ok) {
        console.log('Session restored successfully');
        return true;
      } else {
        clearRefreshToken();
        return false;
      }
    } catch (error) {
      console.error('Auto-login failed:', error);
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();
  
  return refreshPromise;
}

export async function generateAndSaveRefreshToken(): Promise<boolean> {
  try {
    const response = await fetch('/api/auth/refresh-token', {
      method: 'POST',
      credentials: 'include',
    });
    
    if (response.ok) {
      const { refreshToken, expiresAt } = await response.json();
      saveRefreshToken(refreshToken, expiresAt);
      console.log('Refresh token saved for session persistence');
      return true;
    }
    return false;
  } catch (error) {
    console.error('Failed to generate refresh token:', error);
    return false;
  }
}

export function logout(options?: { skipConfirm?: boolean }): void {
  if (!options?.skipConfirm) {
    const confirmed = window.confirm('¿Cerrar sesión?');
    if (!confirmed) return;
  }
  clearRefreshToken();
  // Native shell guarantees its Expo push token is removed server-side
  // for this account before the cookie-bearing redirect happens.
  try {
    getNativeBridge()?.notifySignedOut?.();
  } catch {
    // Ignore: bridge may not be present in plain browsers.
  }
  window.location.href = '/api/logout';
}
